import { ReactNode } from "react";

import { styled } from "@mui/material";

import { parseRows } from "../../graphql/util";
import { GqlError } from "./GqlError";
import { CoreIcon } from "./StatusIcon";

// Child Views
const Card = styled("div")<{ spacer?: string }>`
  padding-top: ${(props) => (props.spacer === "true" ? "51px" : "initial")};
`;

// Default Implementations
const DefaultLoading = () => (
  <Card>
    <CoreIcon type={"working"} color={"inherit"} />
  </Card>
);
const DefaultError = ({ error, small }: { error?: Error | (Error | undefined)[]; small?: boolean }) => (
  <Card>
    <GqlError error={error} small={small} />
  </Card>
);
const DefaultNoResults = () => <Card>No Items</Card>;

type QueryResultProps<TResult, TData> = {
  loading: boolean;
  error: Error | undefined;
  data: TData | undefined;
  nodeName: string;
  loadingElement?: ReactNode;
  emptyElement?: ReactNode;
  errorElement?: ReactNode;
  resultsMapper: (results: TResult, data?: TData) => ReactNode;
  hasResultsFn?: (data: unknown) => boolean;
  small?: boolean;
};

// Rendering
// This handles the loading, error, no rows and results views for calling and presenting data.
// If the query returns paged info it sill automatically show pager controls in the UI and handle paging to prev and next pages.
const QueryResult = <TResult, TData>({
  loading,
  error,
  data,
  nodeName,
  loadingElement,
  emptyElement,
  errorElement,
  resultsMapper,
  hasResultsFn,
  small = false,
}: QueryResultProps<TResult, TData>) => {
  const { results, hasResults } = parseRows<TResult>(data, nodeName, hasResultsFn);

  if (loading) {
    return loadingElement || <DefaultLoading />;
  } else if (error) {
    return errorElement || <DefaultError error={error} small={small} />;
  } else if (hasResults === false) {
    return emptyElement || <DefaultNoResults />;
  } else if (hasResults) {
    return <Card spacer={"false"}>{resultsMapper(results as TResult, data)}</Card>;
  }
};

export { QueryResult };
